import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Lightning, ListChecks, Pulse } from "@phosphor-icons/react";

const LEVEL_STYLES = {
  critical: { bg: "bg-red-500/10",     border: "border-red-700/40",     text: "text-red-400",     dot: "bg-red-400",     bar: "bg-red-500" },
  high:     { bg: "bg-orange-500/10",  border: "border-orange-700/40",  text: "text-orange-400",  dot: "bg-orange-400",  bar: "bg-orange-500" },
  medium:   { bg: "bg-amber-500/10",   border: "border-amber-700/40",   text: "text-amber-400",   dot: "bg-amber-400",   bar: "bg-amber-500" },
  low:      { bg: "bg-emerald-500/10", border: "border-emerald-700/40", text: "text-emerald-400", dot: "bg-emerald-400", bar: "bg-emerald-500" },
};

const SEV_STYLES = {
  critical: { dot: "bg-red-400",     text: "text-red-400" },
  warning:  { dot: "bg-amber-400",   text: "text-amber-400" },
  low:      { dot: "bg-emerald-400", text: "text-emerald-400" },
};

/**
 * Compact inline risk pill — for embedding in cards/tables.
 */
export function RiskPill({ score, level, mode, testid }) {
  if (score == null) return null;
  const s = LEVEL_STYLES[level] || LEVEL_STYLES.low;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${s.bg} ${s.border} ${s.text}`}
      data-testid={testid}>
      <Shield size={9} weight="duotone" />
      Risk {score}
      <span className="opacity-70">· {level}</span>
      {mode && <span className="opacity-50 hidden sm:inline">· {mode}</span>}
    </span>
  );
}

/**
 * Full explainable risk card — score + level + top reasons + recommended actions.
 * Used in Shipment Detail Overview and as Driver Performance card.
 */
export default function RiskCard({ title, icon: Icon, payload, mode, testid, onRefresh, loading, footer }) {
  const score = payload?.risk_score;
  const level = payload?.risk_level || "low";
  const s = LEVEL_STYLES[level] || LEVEL_STYLES.low;

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid={testid}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1.5">
          {Icon ? <Icon size={12} weight="duotone" className="text-teal-400" /> : <Shield size={12} weight="duotone" className="text-teal-400" />}
          {title}
        </h3>
        {(mode || payload?.mode) && (
          <Badge variant="outline" className="border-zinc-700 text-[9px] font-mono uppercase">
            <span className="relative flex h-1.5 w-1.5 mr-1">
              {(payload?.mode === "live" || mode === "live") && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60" />}
              <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${(payload?.mode === "live" || mode === "live") ? "bg-teal-400" : "bg-zinc-500"}`} />
            </span>
            {payload?.mode || mode}
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="text-xs font-mono text-zinc-500 animate-pulse text-center py-4">Calculating risk...</div>
      ) : !payload ? (
        <div className="text-xs text-zinc-600 text-center py-4">No data</div>
      ) : (
        <>
          {/* Score block */}
          <div className={`flex items-center justify-between gap-3 p-3 rounded-sm border ${s.bg} ${s.border}`}>
            <div>
              <p className={`text-3xl font-mono font-bold leading-none ${s.text}`}>
                {score}
                <span className="text-xs text-zinc-500 ml-1 font-normal">/100</span>
              </p>
              <p className={`text-[10px] uppercase tracking-wider mt-1 ${s.text}`}>{level} risk</p>
            </div>
            {/* Bar */}
            <div className="flex-1 max-w-[140px]">
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className={`h-full ${s.bar} transition-all duration-500`} style={{ width: `${score}%` }} />
              </div>
            </div>
          </div>

          {/* Top reasons */}
          {payload.top_reasons?.length > 0 ? (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                <Lightning size={9} /> Top contributing factors
              </p>
              <div className="space-y-1.5">
                {payload.top_reasons.map((f, i) => {
                  const fs = SEV_STYLES[f.severity] || SEV_STYLES.low;
                  return (
                    <div key={i} className="flex items-start gap-2 text-[11px]" data-testid={`risk-factor-${i}`}>
                      <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${fs.dot}`} />
                      <div className="flex-1 min-w-0">
                        <span className={`font-mono font-semibold ${fs.text}`}>{f.factor}</span>
                        <span className="text-zinc-500"> · {f.detail}</span>
                      </div>
                      {f.weight > 0 && (
                        <span className="text-[9px] font-mono text-zinc-600 shrink-0">+{f.weight}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-emerald-400">No risk factors identified</p>
          )}

          {/* Recommended actions */}
          {payload.recommended_actions?.length > 0 && (
            <div className="space-y-2 pt-3 border-t border-zinc-800">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                <ListChecks size={9} /> Recommended actions
              </p>
              <ul className="space-y-1 text-[11px]">
                {payload.recommended_actions.map((r, i) => (
                  <li key={i} className="text-zinc-300 flex gap-2" data-testid={`risk-action-${i}`}>
                    <span className="text-teal-400 shrink-0">→</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {footer}
        </>
      )}
    </Card>
  );
}
